import { claimStatusOpenValues } from "../constants";
import ClaimLeaveData from "./ClaimLeaveData";
import { normalizeDateOfBirth } from "../utils/helpers";

const ORG_GUID =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const openStatusSet = new Set(
    claimStatusOpenValues.map((value) => value.toLowerCase())
);

class Employee {
    _getEmployeeKey = (employee, orgId) => {
        const id = String(employee?.employeeId || "").trim();
        const ssn = String(employee?.taxIdentificationNumber || "").trim();
        const org = String(orgId ?? employee?.orgId ?? "")
            .trim()
            .toLowerCase();

        const parts = [id, org, ssn].filter(Boolean);
        return parts.length > 0 ? parts.join("::") : null;
    };

    // Repeated ids (one per source system) get a #n suffix so each stays distinct.
    getOdsEmployeeKeys = (employees) => {
        const seen = {};
        return employees.map((employee) => {
            const key = this._getEmployeeKey(employee, employee?.orgId);
            if (!key) return null;
            seen[key] = (seen[key] || 0) + 1;
            return seen[key] === 1 ? key : `${key}#${seen[key]}`;
        });
    };

    // ODS org ids are GUIDs; some responses carry the org name in the same field.
    _resolveOrgId = (...candidates) => {
        const values = candidates
            .map((value) =>
                String(value ?? "")
                    .trim()
                    .toLowerCase()
            )
            .filter(Boolean);

        return values.find((value) => ORG_GUID.test(value)) || values[0] || "";
    };

    _isOpenStatus = (statusValue) =>
        openStatusSet.has(
            String(statusValue || "")
                .trim()
                .toLowerCase()
        );

    _extractList = (data) => {
        if (Array.isArray(data)) {
            return data;
        }

        if (Array.isArray(data?.employeeList)) {
            return data.employeeList;
        }

        return [];
    };

    _format = (data) => {
        return {
            Organization: data.OrganizationName,
            OrganizationId: data.OrganizationId,
            PolicyId: "",
            SSN: data.SSN,
            EEID: data.EmployeeID,
            OpenLeave: data.OpenLeaveNumber,
            OpenClaim: data.OpenClaimNumber,
            ClaimSystemCd: null,
            Coverage: data.Coverage,
            LeaveReason: data.LeaveReason,
            FirstName: data.FirstName,
            MiddleName: data.MiddleName,
            LastName: data.LastName,
            DateOfBirth: data.DateOfBirth,
            IsClaimOrLeave: data.IsClaimOrLeave,
            SSNState: "TRUNCATED",
            MasterPartyId: data.MasterPartyId,
            __odsEmployeeHistory: data.__odsEmployeeHistory ?? null,
        };
    };

    format = (data) => {
        const sourceList = this._extractList(data);
        return sourceList.map((el) => this._format(el));
    };

    _getFirstOpen = (items, statusField, statusValue) => {
        if (!items || items.length === 0) return null;
        return items.find((item) => item[statusField] === statusValue) || null;
    };

    _formatODSItem = (item) => {
        const claims = item.Claim || [];
        const leaves = item.Leave || [];

        const firstOpenClaim = this._getFirstOpen(
            claims,
            "derivedClaimStatusDesc",
            "Open"
        );
        const firstOpenLeave = this._getFirstOpen(
            leaves,
            "leaveStatus",
            "Open"
        );

        const orgDetails =
            firstOpenClaim?.srcOrgDetails ||
            firstOpenLeave?.srcOrgDetails ||
            {};

        let isClaimOrLeave = 0;
        if (firstOpenClaim && firstOpenLeave) isClaimOrLeave = 3;
        else if (firstOpenClaim) isClaimOrLeave = 1;
        else if (firstOpenLeave) isClaimOrLeave = 2;

        return {
            Organization: orgDetails.orgName || "",
            OrganizationId: (orgDetails.orgId || "").toLowerCase(),
            PolicyId: "",
            SSN: item.taxIdentificationNumber || "",
            EEID: item.employeeId || "",
            OpenLeave: firstOpenLeave?.leaveId || null,
            OpenClaim: firstOpenClaim?.claimId || null,
            ClaimSystemCd: firstOpenClaim?.claimSystemCd || null,
            Coverage:
                firstOpenClaim?.coverageType ||
                firstOpenLeave?.coverageType ||
                "",
            LeaveReason:
                firstOpenLeave?.leaveReason ||
                firstOpenClaim?.primaryDiagnosisDesc ||
                "",
            FirstName: item.firstName || "",
            MiddleName: "",
            LastName: item.lastName || "",
            DateOfBirth: item.dateOfBirth || "",
            IsClaimOrLeave: isClaimOrLeave,
            SSNState: "TRUNCATED",
            MasterPartyId: this._getEmployeeKey(item, orgDetails.orgId) || "",
        };
    };

    formatODS = (data) => {
        const absenceDetails = data.AbsenceDetail || [];
        return absenceDetails.map((item) => this._formatODSItem(item));
    };

    // ODS-specific transformation methods
    _buildOdsEmployeeHistory = (employee) => {
        return {
            EmployeeDetails: {
                SSN: employee?.taxIdentificationNumber || null,
                FirstName: employee?.firstName || "",
                LastName: employee?.lastName || "",
                DateOfBirth: normalizeDateOfBirth(employee?.dateOfBirth),
                EmployeeID: employee?.employeeId || null,
            },
            ClaimLeaveData: ClaimLeaveData.formatOds(employee),
        };
    };

    _mapOdsEmployeeToEmployeeContracts = (
        employee,
        employeeKey = this._getEmployeeKey(employee, employee?.orgId)
    ) => {
        const claims = employee?.claim || [];
        const leaves = employee?.leave || [];
        const selectedClaims = claims.filter((claim) =>
            this._isOpenStatus(claim?.derivedClaimStatusDesc)
        );
        const selectedLeaves = leaves.filter((leave) =>
            this._isOpenStatus(leave?.leaveStatus)
        );
        const odsEmployeeHistory = this._buildOdsEmployeeHistory(employee);

        const buildBase = (sourceOrgDetails = {}) => ({
            OrganizationName:
                sourceOrgDetails.orgName || employee?.orgName || "",
            OrganizationId: this._resolveOrgId(
                sourceOrgDetails.orgId,
                employee?.orgId
            ),
            SSN: employee?.taxIdentificationNumber || null,
            EmployeeID: employee?.employeeId || null,
            FirstName: employee?.firstName || "",
            MiddleName: employee?.middleName || "",
            LastName: employee?.lastName || "",
            DateOfBirth: employee?.dateOfBirth || null,
            // For ODS payloads, group/render by employee id.
            MasterPartyId: employeeKey,
            __odsEmployeeHistory: odsEmployeeHistory,
        });

        const rows = [];

        selectedLeaves.forEach((leave) => {
            rows.push({
                ...buildBase(leave?.srcOrgDetails || {}),
                OpenLeaveNumber: leave?.leaveId || null,
                OpenClaimNumber: null,
                Coverage:
                    leave?.leaveCoverageTypeDesc || leave?.coverageType || "",
                LeaveReason: leave?.leaveReasonDesc || leave?.leaveReason || "",
                IsClaimOrLeave: 2,
            });
        });

        selectedClaims.forEach((claim) => {
            rows.push({
                ...buildBase(claim?.srcOrgDetails || {}),
                OpenLeaveNumber: null,
                OpenClaimNumber: claim?.claimId || null,
                Coverage: claim?.coverageType || "",
                LeaveReason: "",
                IsClaimOrLeave: 1,
            });
        });

        if (rows.length === 0) {
            rows.push({
                ...buildBase(),
                OpenLeaveNumber: null,
                OpenClaimNumber: null,
                Coverage: "",
                LeaveReason: "",
                IsClaimOrLeave: 0,
            });
        }

        return rows;
    };

    transformOdsEmployeeResponse = (response) => {
        const employees = response?.EmployeeDetail;

        if (!Array.isArray(employees) || employees.length === 0) {
            return { employeeList: [] };
        }

        const keys = this.getOdsEmployeeKeys(employees);

        return {
            employeeList: employees.flatMap((emp, index) =>
                this._mapOdsEmployeeToEmployeeContracts(emp, keys[index])
            ),
        };
    };

    formatOdsSearchResult = (response) => {
        return this.format(this.transformOdsEmployeeResponse(response));
    };
}

export default new Employee();
